class PageHeaderSection extends HTMLElement {
  constructor() {
    super();

    this.isSticky = this.dataset.sticky === "true";
    this.isStickyAlways = this.dataset.stickyAlways === "true";
    this.isTransparent = this.dataset.transparent === "true";
    this.isAlwaysMobileMenu = this.dataset.alwaysMobileMenu === "true";

    // Optional CSS selector for the section over which the header should hide
    this.hideOverSelector = this.dataset.hideOverSelector || null;

    this.classBodyAlwaysMobileMenu = "mobile-nav";

    this.header = document.querySelector(".page-header");
    this.desktopMenuTrigger = document.querySelector(
      ".wt-header__sticky-menu-trigger",
    );
    this.desktopMenuBar = document.querySelector(".wt-drawer--nav");

    this.enabledClass = "sticky-enabled";
    this.showClass = "sticky-show";
    this.desktopHeaderWithMenuBarClass = "page-header--sticky-show-menubar-lg";
  }

  connectedCallback() {
    this._onSignedOutTextClick = () => {
      document.querySelector(".signed-out-avatar-menu")?.click();
    };
    document.querySelector(".signed-out-text")?.addEventListener("click", this._onSignedOutTextClick);

    if (window.Shopify?.designMode) {
      this._onSectionLoad = () => this.captureSearchGutter();
      document.addEventListener("shopify:section:load", this._onSectionLoad);
    }

    this.init();
    this.setupFitNavigation();
  }

  disconnectedCallback() {
    this.teardownFitNavigation();
    this.disableStickyHeader();
    document.querySelector(".signed-out-text")?.removeEventListener("click", this._onSignedOutTextClick);
    if (this._onSectionLoad) {
      document.removeEventListener("shopify:section:load", this._onSectionLoad);
      this._onSectionLoad = null;
    }
  }

  getHeaderHeight() {
    return this.header ? this.header.offsetHeight : 0;
  }

  /** Returns the target element for the hide-over behavior (if any). */
  getHideOverTarget() {
    if (!this.hideOverSelector) return null;
    if (this._hideOverTarget && document.body.contains(this._hideOverTarget)) {
      return this._hideOverTarget;
    }
    this._hideOverTarget =
      document.querySelector(this.hideOverSelector) || null;
    return this._hideOverTarget;
  }

  /**
   * Should the sticky header be hidden because its bottom edge reached
   * the target section's top edge?
   *
   * Geometry reference (viewport coordinates):
   * - header top: usually 0 when sticky is shown
   * - header bottom: header.offsetHeight
   * - section top: target.getBoundingClientRect().top
   *
   * We hide when sectionTop <= headerBottom.
   */
  shouldHideOverSection() {
    const target = this.getHideOverTarget();
    if (!target || !this.header) return false;

    const rect = target.getBoundingClientRect();
    const headerH = this.getHeaderHeight();

    // Small hysteresis to avoid flicker on exact boundary (in px)
    const EPS = 1;

    // True when the section intersects with the header band [0, headerH)
    // i.e. sectionTop < headerBottom && sectionBottom > 0
    const overlapsHeaderBand = rect.top < headerH - EPS && rect.bottom > EPS;

    return overlapsHeaderBand;
  }

  /** Attach / detach a lightweight resize handler to keep things in sync. */
  attachResizeHandler() {
    // Re-run calculations on resize; cheap logic, no throttling needed here
    this._onResize = () => {
      // Re-capture the natural header height for option_1 mobile (no-op
      // when sticky is already engaged so we don't grab the compact height).
      this.setNaturalHeaderHeight();
      // Force potential reflow-based checks in next scroll tick
      // so currentScrollPos logic stays consistent.
      this._stickyOffsetsDirty = true;
      this.scrollHandler?.();
    };
    window.addEventListener("resize", this._onResize, { passive: true });
  }

  detachResizeHandler() {
    if (this._onResize) {
      window.removeEventListener("resize", this._onResize);
      this._onResize = null;
    }
  }

  /**
   * Capture the header's natural (non-sticky) offsetHeight into the
   * --header-natural-height CSS variable on documentElement. Used by
   * option_1 mobile to pad <main> when the header switches to
   * position: fixed once sticky engages — the padding is the constant
   * natural height, so content doesn't reflow when compact mode collapses
   * the header inside the fixed area.
   *
   * Only writes when .sticky-enabled is NOT on the header, otherwise we'd
   * capture the smaller compact height and content would shift.
   */
  setNaturalHeaderHeight() {
    if (!this.header) return;
    if (this.header.classList.contains(this.enabledClass)) return;
    document.documentElement.style.setProperty(
      "--header-natural-height",
      `${this.header.offsetHeight}px`,
    );

    this.captureSearchGutter();
  }


  captureSearchGutter() {
    if (!this.classList.contains("wt-header--v1")) return;
    if (!window.matchMedia("(min-width: 1200px)").matches) return;

    const headerSearchEl = document.querySelector(".wt-header__search");
    if (!headerSearchEl) return;

    document.documentElement.style.removeProperty("--header-search-margin-right");

    requestAnimationFrame(() => {
      const marginRight = window.getComputedStyle(headerSearchEl).marginRight;
      if (marginRight && marginRight !== "0px") {
        document.documentElement.style.setProperty(
          "--header-search-margin-right",
          marginRight,
        );
      }
    });
  }

  setupFitNavigation() {
    const rail = this.querySelector('.bp-nav-rail');
    if (!rail && !this.classList.contains('bp-home-header')) return;
    const list = rail || this.querySelector('.wt-header__nav-teaser__list');
    const logo = this.querySelector('.wt-header__logo');
    const trigger = this.querySelector('.wt-header__nav-teaser .wt-header__menu-trigger');
    const body = this.querySelector('.wt-header__body');
    if (!list || !logo || !trigger || !body) return;

    const update = () => {
      this._fitFrame = null;
      if (!this.isConnected) return;
      const desktop = window.matchMedia('(min-width: 1200px)').matches;
      const crowded = desktop && list.getBoundingClientRect().right + 12 >= logo.getBoundingClientRect().left;
      if (crowded && list.contains(document.activeElement)) trigger.focus();
      const ariaHidden = list.getAttribute('aria-hidden');
      if (this.classList.contains('bp-header-nav-compact') === crowded &&
          list.inert === crowded &&
          (crowded ? ariaHidden === 'true' : ariaHidden === null)) return;
      this.classList.toggle('bp-header-nav-compact', crowded);
      list.inert = crowded;
      if (crowded) list.setAttribute('aria-hidden', 'true');
      else list.removeAttribute('aria-hidden');
    };
    this._scheduleFitNavigation = () => {
      if (!this.isConnected) return;
      if (this._fitFrame == null) this._fitFrame = requestAnimationFrame(update);
    };
    if (typeof ResizeObserver === 'function') {
      this._fitObserver = new ResizeObserver(this._scheduleFitNavigation);
      for (const element of [body, list, logo]) this._fitObserver.observe(element);
    } else if (typeof MutationObserver === 'function') {
      this._fitMutationObserver = new MutationObserver(this._scheduleFitNavigation);
      this._fitMutationObserver.observe(list, {subtree: true, childList: true, attributes: true, attributeFilter: ['hidden']});
    }
    window.addEventListener('resize', this._scheduleFitNavigation, {passive: true});
    document.fonts?.ready.then(this._scheduleFitNavigation);
    this._scheduleFitNavigation();
  }

  teardownFitNavigation() {
    this._fitObserver?.disconnect();
    this._fitObserver = null;
    this._fitMutationObserver?.disconnect();
    this._fitMutationObserver = null;
    if (this._scheduleFitNavigation) window.removeEventListener('resize', this._scheduleFitNavigation);
    if (this._fitFrame != null) cancelAnimationFrame(this._fitFrame);
    this._fitFrame = null;
    this._scheduleFitNavigation = null;
  }

  enableStickyHeader() {
    if (!this.header) {
      console.error("Header element not found for enabling sticky header");
      return;
    }

    document.body.classList.add("page-header-sticky");

    let prevScrollpos = window.pageYOffset;

    const isDesktop = () => window.matchMedia("(min-width: 1200px)").matches;
    const isMenuBarOpen = () =>
      this.header.classList.contains(this.desktopHeaderWithMenuBarClass);
    const isHeaderWithDesktopNav = () =>
      !document.body.classList.contains("mobile-nav");
    const allLLevelsLinks =
      this.desktopMenuBar?.querySelectorAll("a[data-menu-level]");
    const onlyLevel1Links = this.desktopMenuBar?.querySelectorAll(
      "a[data-menu-level='1']",
    );

    const header = document.querySelector("#header");

    const calculateNavbarTopMargin = () => {
      const navbar = document.querySelector("#wt-drawer-nav");
      if (!navbar || !header) return 0;

      const headerHeight = header.offsetHeight;
      const navbarHeight = navbar.offsetHeight;
      const marginTop = headerHeight - navbarHeight;
      const value = `${marginTop}px`;
      if (navbar.style.getPropertyValue("--top-margin") !== value)
        navbar.style.setProperty("--top-margin", value);

      return marginTop;
    };

    const calculateStickyFiltersTopOffset = (value = 0) => {
      const stickyFilters = document.querySelector(
        ".collection__sticky-header",
      );
      const plpWrapper = document.querySelector(".collection-grid-section");

      if (stickyFilters && plpWrapper) {
        const offset = value ?? `${this.header.offsetHeight}px`;
        if (plpWrapper.style.getPropertyValue("--filters-sticky-offset") !== String(offset))
          plpWrapper.style.setProperty("--filters-sticky-offset", offset);
      }
    };

    calculateNavbarTopMargin();
    calculateStickyFiltersTopOffset();
    this.setNaturalHeaderHeight();

    const stickyHeader = {
      show: () => {
        if (stickyHeader.visible && this.header?.classList.contains(this.showClass)) return;
        if (this.header) this.header.classList.add(this.showClass);
        stickyHeader.visible = true;
        stickyHeader.handleBehavior();
      },
      hide: () => {
        if (!stickyHeader.visible && !this.header?.classList.contains(this.showClass)) return;
        if (this.header) this.header.classList.remove(this.showClass);
        stickyHeader.visible = false;
        stickyHeader.handleBehavior();
      },
      enable: () => {
        if (stickyHeader.sentinelInitialized && stickyHeader.enabled &&
            this.header?.classList.contains(this.enabledClass)) return;
        if (this.header) this.header.classList.add(this.enabledClass);
        stickyHeader.enabled = true;
        stickyHeader.sentinelInitialized = true;
        this._stickyOffsetsDirty = true;
        stickyHeader.handleBehavior();
      },
      disable: () => {
        if (stickyHeader.sentinelInitialized && !stickyHeader.enabled &&
            !this.header?.classList.contains(this.enabledClass) &&
            !this.header?.classList.contains(this.showClass)) return;
        if (this.header)
          this.header.classList.remove(this.enabledClass, this.showClass);
        stickyHeader.enabled = false;
        stickyHeader.sentinelInitialized = true;
        this._stickyOffsetsDirty = true;
        stickyHeader.handleBehavior();
      },
      enabled: false,
      sentinelInitialized: false,
      visible: true,
      handleBehavior: () => {
        if (!isHeaderWithDesktopNav() || !isDesktop()) {
          if (this.desktopMenuTrigger) setTabindex([this.desktopMenuTrigger], "-1");
          return;
        }
        if (this.header) {
          stickyHeader.log();
          if (!isMenuBarOpen() && stickyHeader.enabled) {
            setTabindex(allLLevelsLinks, "-1");
          }
          if (isMenuBarOpen() && stickyHeader.enabled) {
            setTabindex(allLLevelsLinks, "0");
          }
          if (!stickyHeader.enabled) {
            setTabindex(onlyLevel1Links, "0");
            if (this.desktopMenuTrigger)
              setTabindex([this.desktopMenuTrigger], "-1");
          } else if (this.desktopMenuTrigger)
            setTabindex([this.desktopMenuTrigger], "0");
        }
      },
      log: () => {},
    };

    // --- Scroll handler with "hide-over-section" override ----------
    this.scrollHandler = () => {
      if (this._stickyFrame != null) return;
      this._stickyFrame = requestAnimationFrame(() => {
        this._stickyFrame = null;
        const currentScrollPos = window.pageYOffset;

        // If we're over the target section, force-hide regardless of isStickyAlways.
        const shouldShow = !this.shouldHideOverSection() &&
          (this.isStickyAlways || prevScrollpos > currentScrollPos);
        const showApplied = this.header?.classList.contains(this.showClass);
        const stateChanged = shouldShow ? !stickyHeader.visible || !showApplied :
          stickyHeader.visible || showApplied;
        const offsetsDirty = this._stickyOffsetsDirty;
        this._stickyOffsetsDirty = false;
        if (offsetsDirty || (shouldShow && stateChanged)) calculateNavbarTopMargin();
        if (shouldShow) stickyHeader.show();
        else stickyHeader.hide();
        if (offsetsDirty && !stateChanged) stickyHeader.handleBehavior();
        if (offsetsDirty || stateChanged)
          calculateStickyFiltersTopOffset(shouldShow ? undefined : 0);

        prevScrollpos = currentScrollPos;
      });
    };

    window.addEventListener("scroll", this.scrollHandler, { passive: true });
    this.attachResizeHandler();

    this._onDesktopMenuClick = (e) => {
      e.preventDefault();
      this.desktopMenuBar?.classList.toggle("wt-drawer--nav-show");
      this.desktopMenuTrigger?.classList.toggle(
        "wt-header__sticky-menu-trigger--active",
      );
      this.header?.classList.toggle(this.desktopHeaderWithMenuBarClass);
      stickyHeader.handleBehavior();
    };
    this.desktopMenuTrigger?.addEventListener("click", this._onDesktopMenuClick);

    const sentinel = document.querySelector(".sticky-header__threshold");
    const handleStickySentinel = (entries) => {
      entries.forEach(({ isIntersecting }) => {
        if (isIntersecting) {
          stickyHeader.disable();
        } else {
          stickyHeader.enable();
        }
      });
    };

    this.stickyHeaderObserver = new IntersectionObserver(handleStickySentinel, {
      root: null,
      rootMargin: `${this.isStickyAlways ? "-160" : "-100"}px 0px 0px 0px`,
      threshold: 0,
    });
    if (sentinel) this.stickyHeaderObserver.observe(sentinel);
  }

  disableStickyHeader() {
    if (this._stickyFrame != null) {
      cancelAnimationFrame(this._stickyFrame);
      this._stickyFrame = null;
    }
    this._stickyOffsetsDirty = false;
    if (this._onDesktopMenuClick) {
      this.desktopMenuTrigger?.removeEventListener("click", this._onDesktopMenuClick);
      this._onDesktopMenuClick = null;
    }
    if (this.header) {
      // Remove classes added by sticky header
      this.header.classList.remove(
        this.enabledClass,
        this.showClass,
        this.desktopHeaderWithMenuBarClass,
      );
      document.body.classList.remove("page-header-sticky");
      this.desktopMenuBar?.classList.remove("wt-drawer--nav-show");
      this.desktopMenuTrigger?.classList.remove(
        "wt-header__sticky-menu-trigger--active",
      );
    }

    // Remove the scroll event listener
    if (this.scrollHandler) {
      window.removeEventListener("scroll", this.scrollHandler);
      this.scrollHandler = null;
    }

    // Detach resize handler
    this.detachResizeHandler();

    // Disconnect the IntersectionObserver
    if (this.stickyHeaderObserver) {
      this.stickyHeaderObserver.disconnect();
      this.stickyHeaderObserver = null;
    }
  }

  init() {
    if (this.isSticky) {
      this.enableStickyHeader();
    } else {
      this.disableStickyHeader();
    }

  }
}

customElements.define("page-header", PageHeaderSection);
